<script setup lang="ts">
	import { computed, onBeforeUnmount, onMounted, watch } from 'vue';
	import { useRoute, useRouter } from 'vue-router';
	import { useI18n } from 'vue-i18n';
	import Header from '@/components/Header.vue';
	import EventPresentation from '@/components/EventPresentation.vue';
	import leftArrow from '@/assets/presentation/left-arrow.svg';
	import bline from '@/assets/bline.svg';
	import { useEventListStore, useMapStoreData } from '@/stores/index';
	import { formatEventDateFacebookStyle } from '@/composables/formatEventDate';

	const { t } = useI18n();
	const route = useRoute();
	const router = useRouter();
	const routeIndex = computed(() => {
		const raw = route.params.id;
		const first = Array.isArray(raw) ? raw[0] : raw;
		const n = typeof first === 'string' ? Number(first) : typeof first === 'number' ? first : NaN;
		return Number.isFinite(n) ? n : null;
	});
	const mapStore = useMapStoreData();
	const eventListStore = useEventListStore();
	const title = computed(() => {
		if (route.name === 'eventListModule' && routeIndex.value != null) {
			return mapStore.routes[routeIndex.value]?.title || 'general.eventlist';
		}
		return 'general.eventlist';
	});

	const items = computed(() => eventListStore.items);
	const activeEventPresentation = computed({
		get: () => eventListStore.activeEventPresentation,
		set: (value) => eventListStore.setActiveEventPresentation(value),
	});

	function goHome() {
		router.push('/');
	}

	function displayTitle(item: any) {
		return item?.title || '';
	}

	function displayStart(item: any) {
		return formatEventDateFacebookStyle(item?.start, mapStore.currentLanguage || 'en');
	}

	function displayDescription(item: any) {
		return item?.description || '';
	}

	function displayImage(item: any) {
		return item?.image_m || '';
	}

	function openEvent(item: any) {
		activeEventPresentation.value = item;
	}

	function closeEventPresentation() {
		activeEventPresentation.value = null;
	}

	onMounted(async () => {
		mapStore.hideMap = true;
		await eventListStore.loadEventsForLanguage();
		await eventListStore.checkForUpdates();
		eventListStore.startPolling(15000);
	});

	onBeforeUnmount(() => {
		eventListStore.stopPolling();
		eventListStore.setActiveEventPresentation(null);
	});

	watch(
		() => mapStore.currentLanguage,
		async () => {
			await eventListStore.syncOnLanguageChange();
		}
	);
</script>

<template>
	<div class="event-list-view">
		<Header v-once :setupHeader="{ centerIcon: false, orderFilter: false, search: false, backArrow: false }"></Header>
		<EventPresentation :activeEvent="activeEventPresentation" @close="closeEventPresentation" />
		<div class="scenarios">
			<div class="scenarios-container">
				<div class="sheader">
					<h3 class="title">
						<img @click="goHome()" :src="leftArrow" alt="" />
						{{ t(title) }}
					</h3>
					<img :src="bline" alt="" />
				</div>

				<div class="scroller">
					<div v-if="eventListStore.isLoading && items.length === 0" class="status">{{ t('general.loading') }}</div>
					<div v-else-if="eventListStore.error" class="status">{{ eventListStore.error }}</div>
					<template v-else-if="items.length > 0">
						<div
							v-for="(item, index) in items"
							:key="item.id || index"
							class="scenarios-card event event-list-card"
							@click="openEvent(item)"
						>
							<div
								class="bgi"
								:style="displayImage(item) ? { backgroundImage: 'url(' + displayImage(item) + ')' } : {}"
							></div>
							<div class="content">
								<div class="address" v-if="displayStart(item)">
									{{ displayStart(item) }}
								</div>
								<h3 class="title">{{ displayTitle(item) }}</h3>
								<p class="event-list-description" v-if="displayDescription(item)">
									{{ displayDescription(item) }}
								</p>
							</div>
						</div>
					</template>
					<div v-else class="status">{{ t('general.noresult') }}</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
	@import '../assets/style/mixins.scss';
	@import '../assets/style/_ScenariosView.scss';
	@import '../assets/style/components/_scenariosCard.scss';

	.status {
		padding: get-vw(24px);
		color: #2e3d45;
	}

	.event-list-card {
		height: get-vw(500px);
		display: flex;
		flex-direction: column;

		.bgi {
			flex: 0 0 get-vw(275px);
		}

		.content {
			flex: 1 1 auto;
			display: flex;
			flex-direction: column;
			overflow: visible;
		}

		.title {
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
			overflow: hidden;
			margin-bottom: get-vw(10px);
		}

		.event-list-description {
			margin-top: 0;
			margin-bottom: 0;
			font-size: get-vw(15px);
			line-height: 1.35;
			color: #2e3d45;
			display: -webkit-box;
			-webkit-line-clamp: 3 !important;
			line-clamp: 3;
			-webkit-box-orient: vertical;
			overflow: hidden;
			max-height: calc(3 * 1.35em);
			min-height: calc(3 * 1.35em);
		}
	}

	@media only screen and (orientation: portrait) {
		.event-list-card {
			height: get-vw(690px, 1080);
			.bgi {
				flex: 0 0 get-vw(415px, 1080);
			}
		}
	}
</style>
